// ============================================================
// STRUCTURED NARRATIVE - Types and builder for collapsible
// combat log entries with detail sections.
//
// Backward compatible: old entries render via NarrativeEntry.
// New entries with `structured` field render via
// NarrativeEntryStructured.
//
// Zero React dependency.
// ============================================================
import { UNDYING_REASON } from './undyingReason';
import type { NarrativeInput, HitSummary, DefenceStepSummary } from './narrativeFormatter';
import type { WeaponEffectDetail } from './weaponEffects';

// ---- Types ----

interface RollDetail {
  label: string;
  roll: number;
  target: number;
  success: boolean;
}

export interface ModifierDetail {
  label: string;
  value: number;
}

export interface DamageBreakdown {
  hit: number;
  location: string;
  /** The location's engine id (head, rightArm, hull...); absent on rows written before v10.5.2. */
  locationId?: string;
  raw: number;
  ap: number;
  tb: number;
  effective: number;
  crit?: string;
  ffBlocked?: boolean;
  // Enrichment fields (Phase 5 - present when HitResult has breakdown data)
  diceStr?: string;
  damageType?: string;
  strengthBonus?: number;
  talentDmgBonus?: number;
  accurateDice?: number;
  devastatingBonus?: number;
  maximalDmgBonus?: number;
  scatterMod?: number;
  forceBonus?: number;
  /** The predicate family's damage share (Witchbane, Titanbane, Momentum... v8.285.0). */
  predicateBonus?: number;
  /** Witchbane/Purifier's penetration share (v8.285.0). */
  predicatePen?: number;
  /** What the target's exposures changed (v8.368.9): damage, penetration, toughness, a cap, the crit. */
  exposureDmg?: number;
  exposurePen?: number;
  exposureTB?: number;
  exposureDmgCap?: number;
  exposureCrit?: number;
  baseAP?: number;
  machineAP?: number;
  naturalAP?: number;
  coverAP?: number;
  basePen?: number;
  effectivePen?: number;
  baseTB?: number;
  utMultiplier?: number;
  fellingReduction?: number;
  /** Soak the protagonist cap removed - explains a base AP below the sheet's. */
  soakCapReduction?: number;
  daemonicProt?: number;
  righteousFury?: boolean;
  rfRoll?: number;
  rfConfirmed?: boolean;
  rfAutoConfirmed?: boolean;
  rfSeverity?: number;
  ffRoll?: number;
  ffOverloaded?: boolean;
  ffProtection?: number;
  ffOverloadThreshold?: number;
}

interface CriticalEffect {
  location: string;
  severity: number;
  text: string;
  conditions: string[];
}

export interface StatusSummary {
  totalDamage: number;
  woundsAfter: number;
  woundsMax: number;
  isHorde: boolean;
  magDmg?: number;
  magAfter?: number;
  magMax?: number;
  isDead?: boolean;
  isDestroyed?: boolean;
  /** Undying refused a death this attack dealt (the map plays its fall and rise, plan body-map M2). */
  cheatedDeath?: boolean;
}

export interface StructuredDetail {
  rolls?: RollDetail[];
  modifiers?: ModifierDetail[];
  damageBreakdown?: DamageBreakdown[];
  defenceDetails?: DefenceStepSummary[];
  weaponEffects?: WeaponEffectDetail[];
  criticalEffects?: CriticalEffect[];
  specialNotes?: string[];
  status?: StatusSummary;
}

export type AttackOutcome = 'hit' | 'partial' | 'deflected' | 'blocked' | 'miss' | 'jam';

export interface StructuredNarrativeEntry {
  summary: string;
  detail: StructuredDetail;
  outcome?: AttackOutcome;
  effectTags?: string[];
}

// ---- Outcome classification ----

export const OUTCOME_LABELS: Record<AttackOutcome, string> = {
  hit: 'HIT!',
  partial: 'PARTIAL HIT',
  deflected: 'DEFLECTED',
  blocked: 'BLOCKED',
  miss: 'MISS',
  jam: 'JAM!',
};

type AttackResult = NonNullable<NarrativeInput['result']>;

/** Hits one defence roll avoided: its `avoided` count when it succeeded, else 0. */
function savedBy(d: { success: boolean; avoided: number } | null | undefined): number {
  return d?.success ? d.avoided : 0;
}

/** Hits the defence avoided: the chain's successes, or the legacy dodge + parry. */
function avoidedByDefence(result: AttackResult): number {
  const defenceSteps = result.defenceChainSteps || [];
  if (defenceSteps.length > 0) return defenceSteps.reduce((sum, s) => sum + savedBy(s), 0);
  return savedBy(result.dodgeResult) + savedBy(result.parryResult);
}

const isFieldBlocked = (h: HitSummary): boolean => !!h.ffBlocked;

/** True when the defence avoided a hit or the force field stopped one. */
function someDefended(result: AttackResult, hits: HitSummary[]): boolean {
  return avoidedByDefence(result) > 0 || hits.some(isFieldBlocked);
}

/** The outcome of an attack that connected: blocked, untouched, deflected or partial. */
function landedOutcome(result: AttackResult, totalDamage: number): AttackOutcome {
  const hits = result.hitsAfterDefence || [];
  if (hits.length > 0 && hits.every(isFieldBlocked)) return 'blocked';
  if (!someDefended(result, hits)) return 'hit';
  if (totalDamage === 0) return 'deflected';
  return totalDamage > 0 ? 'partial' : 'hit';
}

/**
 * Classify an attack outcome for visual treatment.
 * Inspects defence results, force field, and total damage.
 */
export function classifyOutcome(result: NarrativeInput['result'], totalDamage: number): AttackOutcome {
  if (!result) return 'miss';
  if (result.jam) return 'jam';
  if (!result.success) return 'miss';
  return landedOutcome(result, totalDamage);
}

/**
 * A weapon effect's tag (Flame, Toxic, Shock, Concussive, Snare, Corrosive, Haywire,
 * Spray, Force). A failed resistance test (success === false) means the effect landed.
 */
const weaponEffectTag = (e: WeaponEffectDetail): string => e.icon + ' ' + e.name + (e.success === false ? '!' : '');

/** Tags for the events that are not plain weapon effects; null means too routine to tag. */
const EVENT_TAGS = new Map<string, (e: WeaponEffectDetail) => string | null>([
  ['Death', () => '\uD83D\uDC80 DEAD'],
  ['Force Field', (e) => (e.result.includes('OVERLOADED') ? '\u26A1 FF Overloaded' : weaponEffectTag(e))],
  ['Horde', () => null],
  ['Critical', () => null],
  ['Immunity', () => null],
  ['Power Field', (e) => (e.result.includes('DESTROYED') ? '\u2694 Weapon Destroyed' : null)],
]);

/** The collapsed tag for one contained event, or null when it earns none. */
function effectTag(e: WeaponEffectDetail): string | null {
  return (EVENT_TAGS.get(e.name) ?? weaponEffectTag)(e);
}

/**
 * Generate compact effect tags for the collapsed summary from contained events.
 */
export function generateEffectTags(containedEvents?: WeaponEffectDetail[]): string[] | undefined {
  if (!containedEvents || containedEvents.length === 0) return undefined;
  const tags = containedEvents.map(effectTag).filter((t): t is string => t !== null);
  return tags.length > 0 ? tags : undefined;
}

// ---- Dice formatting ----

interface DiceGroupLike {
  type: string;
  value?: number;
  rolls?: number[];
  sides?: number;
  countRolls?: number[];
  tornDie?: number | null;
}

/** One dice group as text: a flat value, a nested roll, or dice with any Tearing drop. */
function formatDiceGroup(g: DiceGroupLike): string {
  if (g.type === 'flat') return String(g.value ?? 0);
  if (!g.rolls || g.rolls.length === 0) return '?';
  if (g.type === 'nested')
    return '[' + (g.countRolls || []).join(',') + ']d' + (g.sides ?? '?') + ':[' + g.rolls.join(',') + ']';
  return '[' + g.rolls.join(',') + ']' + (g.tornDie != null ? ' (dropped ' + g.tornDie + ')' : '');
}

/**
 * Format a DiceResult into a compact string like "[3,7] + 5".
 * Handles flat, dice, and nested groups plus Tearing torn die.
 */
export function formatDiceResult(dmgRoll: { results: DiceGroupLike[]; total: number }): string {
  return dmgRoll.results.map(formatDiceGroup).join(' + ');
}

// ---- Builder ----

type BreakdownKey = keyof DamageBreakdown;
type Keep = (v: unknown) => boolean;

const positive: Keep = (v) => (v as number) > 0;
const nonZero: Keep = (v) => v != null && v !== 0;
const present: Keep = (v) => v != null;
const truthy: Keep = (v) => !!v;

/**
 * The optional numeric rows a hit carries into its breakdown, each with the test
 * that decides whether it is worth showing. The ORDER is the breakdown's key
 * order (the log is stored and compared as JSON), so append, never reorder.
 * The exposure rows (v8.368.9) are signed, since Soft is -2 and Hardy -2.
 */
const COPIED_ROWS: readonly [BreakdownKey, Keep][] = [
  ['strengthBonus', positive],
  ['talentDmgBonus', positive],
  ['accurateDice', positive],
  ['devastatingBonus', positive],
  ['maximalDmgBonus', positive],
  ['scatterMod', nonZero],
  ['forceBonus', positive],
  ['predicateBonus', positive],
  ['predicatePen', positive],
  ['exposureDmg', nonZero],
  ['exposurePen', nonZero],
  ['exposureTB', nonZero],
  ['exposureDmgCap', nonZero],
  ['exposureCrit', nonZero],
  ['baseAP', present],
  ['machineAP', present],
  ['naturalAP', present],
  ['coverAP', present],
  ['basePen', present],
  ['effectivePen', present],
  ['baseTB', present],
  ['utMultiplier', present],
  ['fellingReduction', present],
  ['soakCapReduction', truthy],
  ['daemonicProt', present],
];

/** Righteous Fury and force-field rows - present only when the hit had them. */
function addFuryAndFieldRows(breakdown: DamageBreakdown, h: HitSummary): void {
  if (h.righteousFury) {
    breakdown.righteousFury = true;
    breakdown.rfRoll = h.rfRoll as number;
    breakdown.rfConfirmed = h.rfConfirmed as boolean;
    breakdown.rfSeverity = h.rfSeverity as number;
    if (h.rfAutoConfirmed) breakdown.rfAutoConfirmed = true; // Deathwatch Training vs Xenos
  }
  if ((h.ffRoll as number) > 0) {
    breakdown.ffRoll = h.ffRoll as number;
    breakdown.ffProtection = h.ffProtection as number;
    breakdown.ffOverloadThreshold = h.ffOverloadThreshold as number;
  }
  if (h.ffBlocked) breakdown.ffOverloaded = (h.ffOverloaded as boolean) || false;
}

/** One hit's damage breakdown row (1-based `hit`), enriched from the HitResult's breakdown fields. */
function hitBreakdown(h: HitSummary, i: number): DamageBreakdown {
  const breakdown: DamageBreakdown = {
    hit: i + 1,
    location: h.location?.name || '?',
    // The id too (plan body-map, R4): a plate plays the hit by it; the name is for reading.
    ...(h.location?.id ? { locationId: h.location.id } : {}),
    raw: h.rawDamage || 0,
    ap: h.effectiveAP || 0,
    tb: h.tb || 0,
    effective: h.effectiveDamage || 0,
    ffBlocked: h.ffBlocked || false,
  };
  if (h.crit) breakdown.crit = 'Sev ' + (h.critSev || 0) + ': ' + (h.crit.text || '').substring(0, 60);
  const dmgRoll = h.dmgRoll as { results: DiceGroupLike[]; total: number } | undefined;
  if (dmgRoll) breakdown.diceStr = formatDiceResult(dmgRoll);
  if (h.damageType != null) breakdown.damageType = h.damageType as string;
  const rows = breakdown as unknown as Record<string, unknown>;
  for (const [key, keep] of COPIED_ROWS) {
    if (keep(h[key])) rows[key] = h[key];
  }
  addFuryAndFieldRows(breakdown, h);
  return breakdown;
}

/** The critical effects among the hits, one per critting hit. */
function criticalEffects(hits: HitSummary[]): CriticalEffect[] {
  return hits
    .filter((h: HitSummary) => h.crit)
    .map((h: HitSummary) => ({
      location: h.location?.name || '?',
      severity: h.critSev || 0,
      text: h.crit?.text || '',
      conditions: [],
    }));
}

/** A roll row from a defence roll. */
function defenceRoll(label: string, d: { roll: number; skill: number; success: boolean }): RollDetail {
  return { label, roll: d.roll, target: d.skill, success: d.success };
}

/** The defence rolls: every chain step, or the legacy dodge then parry. */
function defenceRolls(r: AttackResult): RollDetail[] {
  if (r.defenceChainSteps && r.defenceChainSteps.length > 0) {
    return r.defenceChainSteps.map((step) => defenceRoll(step.label, step));
  }
  const rolls: RollDetail[] = [];
  if (r.dodgeResult) rolls.push(defenceRoll('Dodge', r.dodgeResult));
  if (r.parryResult) rolls.push(defenceRoll('Parry', r.parryResult));
  return rolls;
}

/** The wound/horde state after the attack. */
function statusSummary(entry: NarrativeInput, containedEvents?: WeaponEffectDetail[]): StatusSummary {
  return {
    totalDamage: entry.totalDamage || 0,
    woundsAfter: entry.woundsAfter ?? 0,
    woundsMax: entry.woundsMax ?? 0,
    isHorde: !!entry.isHorde,
    magDmg: entry.magDmg as number | undefined,
    magAfter: entry.magAfter as number | undefined,
    magMax: entry.magMax as number | undefined,
    isDead: !!containedEvents?.some((e) => e.name === 'Death'),
    cheatedDeath: !!containedEvents?.some((e) => e.result === UNDYING_REASON),
    isDestroyed: !!entry.isHorde && ((entry.magAfter as number) ?? 0) <= 0 && ((entry.magDmg as number) ?? 0) > 0,
  };
}

/** The damage breakdown, then the critical effects when any hit critted. */
function addDamageSections(detail: StructuredDetail, hits: HitSummary[]): void {
  detail.damageBreakdown = hits.map(hitBreakdown);
  const crits = criticalEffects(hits);
  if (crits.length > 0) detail.criticalEffects = crits;
}

const nonEmpty = <T>(list: T[] | undefined): list is T[] => !!list && list.length > 0;

/**
 * The detail sections of a successful attack. Key order is the insertion order
 * below (rolls, modifiers, defence, damage, crits, notes, effects, status).
 */
function hitDetail(entry: NarrativeInput, r: AttackResult, containedEvents?: WeaponEffectDetail[]): StructuredDetail {
  const attackRoll = { label: 'Attack Roll', roll: r.attackRoll || 0, target: r.modifiedSkill || 0, success: true };
  const detail: StructuredDetail = { rolls: [attackRoll, ...defenceRolls(r)] };
  if (nonEmpty(entry.skillBreakdown)) detail.modifiers = entry.skillBreakdown as ModifierDetail[];
  if (nonEmpty(r.defenceChainSteps)) detail.defenceDetails = r.defenceChainSteps;
  if (nonEmpty(r.hitsAfterDefence)) addDamageSections(detail, r.hitsAfterDefence);
  if (nonEmpty(r.specialNotes)) detail.specialNotes = r.specialNotes;
  if (nonEmpty(containedEvents)) detail.weaponEffects = containedEvents;
  detail.status = statusSummary(entry, containedEvents);
  return detail;
}

/** True when a horde lost magnitude - the log then reports magnitude, not wounds. */
export function hordeLostMagnitude(entry: NarrativeInput): boolean {
  return !!entry.isHorde && (entry.magDmg || 0) > 0;
}

/** The one-line summary of a successful attack, led by its outcome label. */
function hitSummaryLine(entry: NarrativeInput, r: AttackResult, outcome: AttackOutcome): string {
  const totalDmg = entry.totalDamage || 0;
  let summary = OUTCOME_LABELS[outcome] + ' ' + (entry.slotName || '?') + ' (' + (entry.fireMode || '?') + ')';
  summary += ' ' + r.dos + ' DoS = ' + r.totalHits + ' hit(s)';
  if (totalDmg > 0) summary += ', ' + totalDmg + ' wounds';
  if (hordeLostMagnitude(entry)) summary += ', Mag -' + entry.magDmg;
  return summary;
}

/** A jam: a summary only, no detail sections. */
function jamEntry(entry: NarrativeInput, r: AttackResult): StructuredNarrativeEntry {
  const overheat = r.overheatDmg ? ' (overheats: ' + r.overheatDmg + ' E)' : '';
  return { summary: 'JAM! ' + (entry.slotName || '?') + ' jams' + overheat, detail: {}, outcome: 'jam' };
}

/** A miss: the summary and the attack roll that failed. */
function missEntry(entry: NarrativeInput, r: AttackResult): StructuredNarrativeEntry {
  const summary =
    'MISS ' +
    (entry.slotName || '?') +
    ' rolled ' +
    r.attackRoll +
    ' vs ' +
    r.modifiedSkill +
    ' (' +
    (r.dof || 0) +
    ' DoF)';
  return {
    summary,
    detail: {
      rolls: [{ label: 'Attack Roll', roll: r.attackRoll || 0, target: r.modifiedSkill || 0, success: false }],
    },
    outcome: 'miss',
  };
}

/**
 * Build a structured narrative entry from an attack result.
 * Returns null for non-attack entries (info, or an attack with no result).
 */
export function buildStructuredEntry(
  entry: NarrativeInput,
  containedEvents?: WeaponEffectDetail[],
): StructuredNarrativeEntry | null {
  const r = entry.result;
  if (!r || entry.type !== 'attack-result') return null;
  if (r.jam) return jamEntry(entry, r);
  if (!r.success) return missEntry(entry, r);

  const detail = hitDetail(entry, r, containedEvents);
  const outcome = classifyOutcome(r, entry.totalDamage || 0);
  const effectTags = generateEffectTags(containedEvents);
  return { summary: hitSummaryLine(entry, r, outcome), detail, outcome, effectTags };
}
